import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Wallet, ArrowRight } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";

function formatMoney(cents, currency = "eur") {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

export default function TreasuryWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/treasury");
        setData(data);
      } catch (e) {
        // silently hide widget if not authorized
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) return null;

  const positive = (data.delta_pct ?? 0) >= 0;
  const chartData = data.series.map((s) => ({
    day: s.day,
    total: s.cumulative_cents / 100,
    daily: s.amount_cents / 100,
  }));

  return (
    <Link
      to="/manage/paiements"
      data-testid="treasury-widget"
      className="block rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-slate-900 to-[#0B0F17] p-5 sm:p-6 hover:border-emerald-500/40 transition-colors relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono-stat uppercase tracking-[0.25em] text-emerald-300 flex items-center gap-1.5 mb-1">
              <Wallet className="w-3 h-3" /> Trésorerie du mois
            </div>
            <div className="text-[10px] uppercase font-mono-stat text-slate-500 tracking-widest">
              {data.month_label}
            </div>
          </div>
          <div className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-[11px] font-mono-stat uppercase">
            Voir <ArrowRight className="w-3 h-3" />
          </div>
        </div>

        <div className="mt-4 flex items-end gap-3 flex-wrap">
          <div className="font-display font-black text-4xl sm:text-5xl text-white leading-none">
            {formatMoney(data.this_month_total_cents, data.currency)}
          </div>
          {data.delta_pct !== null && data.delta_pct !== undefined && (
            <div
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-mono-stat font-bold ${
                positive
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  : "bg-rose-500/15 text-rose-300 border border-rose-500/40"
              }`}
            >
              {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {positive ? "+" : ""}{data.delta_pct}%
            </div>
          )}
          <div className="text-[11px] text-slate-500 font-mono-stat pb-1">
            {data.this_month_count} paiement{data.this_month_count > 1 ? "s" : ""} · mois précédent {formatMoney(data.prev_month_total_cents, data.currency)}
          </div>
        </div>

        <div className="mt-4 h-24 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="treasuryFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" hide />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  background: "#0B0F17",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  fontSize: 11,
                }}
                formatter={(v) =>
                  new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: (data.currency || "eur").toUpperCase(),
                  }).format(v)
                }
                labelFormatter={(d) => `Jour ${d}`}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#22C55E"
                strokeWidth={2}
                fill="url(#treasuryFill)"
                animationDuration={600}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Link>
  );
}
